import { Injectable, inject, signal } from '@angular/core';
import { Settings, ChatPersistenceService } from './chat-persistence.service';
import { AuthService } from './auth.service';

/** Synchronizes non-secret preferences and manages the separate Vault-backed key. */
@Injectable({ providedIn: 'root' })
export class SettingsSyncService {
  private readonly auth = inject(AuthService);
  private readonly persistence = inject(ChatPersistenceService);

  readonly openAiKeyConfigured = signal(false);
  readonly error = signal<string | null>(null);

  async load(): Promise<void> {
    await this.auth.waitUntilInitialized();
    const client = this.auth.supabase;
    const user = this.auth.user();
    if (!client || !user) return;

    const [{ data, error }, { data: hasKey, error: keyError }] = await Promise.all([
      client.from('user_settings').select('settings').eq('user_id', user.id).maybeSingle(),
      client.rpc('has_openai_api_key'),
    ]);
    if (error || keyError) {
      this.error.set(error?.message ?? keyError?.message ?? 'Could not load synced settings.');
      return;
    }
    if (data?.settings && this.isSettings(data.settings)) this.persistence.saveSettings(data.settings);
    this.openAiKeyConfigured.set(hasKey === true);
    this.error.set(null);
  }

  async save(settings: Settings): Promise<void> {
    this.persistence.saveSettings(settings);
    await this.auth.waitUntilInitialized();
    const client = this.auth.supabase;
    const user = this.auth.user();
    if (!client || !user) return;

    const { error } = await client.from('user_settings').upsert({
      user_id: user.id,
      settings,
      updated_at: new Date().toISOString(),
    });
    if (error) this.error.set(error.message);
    else this.error.set(null);
  }

  async saveOpenAiKey(apiKey: string): Promise<void> {
    const client = this.auth.supabase;
    if (!client || !this.auth.user()) throw new Error('Sign in before saving an API key.');
    const { error } = await client.rpc('save_openai_api_key', { p_api_key: apiKey });
    if (error) throw new Error(error.message);
    this.openAiKeyConfigured.set(true);
  }

  async removeOpenAiKey(): Promise<void> {
    const client = this.auth.supabase;
    if (!client || !this.auth.user()) throw new Error('Sign in before removing an API key.');
    const { error } = await client.rpc('delete_openai_api_key');
    if (error) throw new Error(error.message);
    this.openAiKeyConfigured.set(false);
  }

  /** The proxy uses this to decide whether OpenAI requests must stay server-side. */
  useOpenAiProxy(): boolean {
    return this.openAiKeyConfigured();
  }

  private isSettings(value: unknown): value is Settings {
    return !!value && typeof value === 'object' && typeof (value as Partial<Settings>).mainApiUrl === 'string';
  }
}
